import axios from 'axios';
import BaseService from '@/ecm/common/BaseService.js';
import store from '@/store';
import Utils from '@/ecm/common/Utils.js';
var qs = require('qs');

// initial state
const state = {
    token: '',
    user: '',
    userMenu: '',
    userContentTypes: [],
    userSavedSearches: [],
    localization: null,
    apiUrl: '',
    searchOperators: [],
    searchStandardFields: [],
    loginParams: null,
    defaultLanguage: null,
    userDelegations: [],
    newsletter: {
        show: false
    },
};

const getters = {
    isLoggedIn: state => !!state.token,
    currentRole: state => state.user && state.user.IsAdmin ? 0 : 1,
    user: state => state.user,
    userMenu: state => state.userMenu,
    userContentTypes: state => state.userContentTypes,
    userSavedSearches: state => state.userSavedSearches,
    token: state => state.token,

    localization: state => state.localization,
    apiUrl: state => state.apiUrl,
    searchOperators: state => state.searchOperators,
    searchStandardFields: state => state.searchStandardFields,
    loginParams: state => state.loginParams,
    defaultLanguage: state => state.defaultLanguage,
    userDelegations: state => state.userDelegations,
    newsletter: state => state.newsletter,
};
const mutations = {
    SET_TOKEN(state, token) {
        state.token = token;
    },
    SET_USER(state, user) {
        state.user = user;
    },
    CLEAR_USER(state) {
        state.token = '';
        state.user = {};
        state.userMenu = {};
        state.userContentTypes = [];
        state.userSavedSearches = [];
        state.userDelegations = [];
    },
    SET_LOCALIZATION(state, localization) {
        state.localization = localization;
    },
    SET_USER_MENU(state, userMenu) {
        state.userMenu = userMenu;
    },
    SET_USER_CONTENT_TYPE(state, userContentTypes) {
        state.userContentTypes = userContentTypes;
    },
    SET_USER_SAVED_SEARCHES(state, userSavedSearches) {
        state.userSavedSearches = userSavedSearches;
    },
    SET_SEARCH_OPERATORS(state, searchOperators) {
        state.searchOperators = searchOperators;
    },
    SET_SEARCH_STANDAR_FIELDS(state, searchStandardFields) {
        state.searchStandardFields = searchStandardFields;
    },
    SET_API_URL(state, apiUrl) {
        state.apiUrl = apiUrl;
    },
    SET_LOGIN_PARAMS(state, loginParams) {
        state.loginParams = loginParams;
    },
    SET_NEWSLETTER(state, newsletter) {
        state.newsletter = newsletter;
    },
    SET_DEFAULT_LANGUAGE(state, defaultLanguage) {
        state.defaultLanguage = defaultLanguage;
    },
    SET_USER_DELEGATION(state, userDelegations) {
        state.userDelegations = userDelegations;
    },
};

const actions = {
    setApiUrl({ commit }, apiUrl) {
        commit('SET_API_URL', apiUrl);
    },
    setToken({ commit, dispatch }, token) {
        axios.defaults.headers.common.Authorization = 'bearer ' + token;
        commit('SET_TOKEN', token);
        Promise.all([
            dispatch('getUser').then(() => {
                dispatch('postCulture');
                return dispatch('getLocalization');
            }),
            dispatch('getUserMenu'),
            dispatch('getUserContentTypes'),
            dispatch('getUserSavedSearches'),
            dispatch('getOperators'),
            dispatch('getStandardFields'),
            dispatch('getUserDelegations'),
        ]);
    },
    setLoginParams({ commit }, loginParams) {
        commit('SET_LOGIN_PARAMS', loginParams);
    },
    setNewsletter({ commit }, newsletter) {
        const newsletterWithDefaults = {
            show: false,
            ...newsletter
        };

        commit('SET_NEWSLETTER', newsletterWithDefaults);
    },
    setDefaultLanguage({ commit }, defaultLanguage) {
        commit('SET_DEFAULT_LANGUAGE', defaultLanguage);
    },
    login({ commit, dispatch }, user) {
        return new Promise((resolve, reject) => {
            user.grant_type = 'password';
            user.source = 1;
            user.language = Utils.mix.getLanguage();

            axios({
                url: `${store.getters['account/apiUrl']}/token`,
                data: qs.stringify(user),
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
            })
            .then(resp => {
                const token = resp.data.access_token;
                axios.defaults.headers.common.Authorization = 'bearer ' + token;
                commit('SET_TOKEN', token);

                Promise.all([
                    dispatch('getUser').then(() => {
                        dispatch('postCulture');
                        return dispatch('getLocalization');
                    }),
                    dispatch('getUserMenu'),
                    dispatch('getUserContentTypes'),
                    dispatch('getUserSavedSearches'),
                    dispatch('getOperators'),
                    dispatch('getStandardFields'),
                    dispatch('getUserDelegations'),
                ]).finally(() => {
                    resolve(resp.data);
                });
            }).catch(err => {
                if (user.impersonated_user_login == null) {
                    commit('SET_TOKEN', null);
                    commit('SET_USER', null);
                }
                reject(err);
            });
        });
    },
    logout({ commit }) {
        return new Promise(resolve => {
            if (store.getters['account/token']) {
                BaseService.post({ url: 'account/logout' }).then((result) => {
                    resolve();
                })
                    .catch(() => {
                    })
                    .then(() => {
                        //always
                        delete axios.defaults.headers.common.Authorization;
                        commit('CLEAR_USER');
                        resolve();
                    });
            } else {
                delete axios.defaults.headers.common.Authorization;
                commit('CLEAR_USER');
                resolve();
            }
        });
    },
    getUser({ commit }) {
        return BaseService.get({ url: 'user/get-current-user' }).then(result => {
            commit('SET_USER', result);
        });
    },
    getUserMenu({ commit }) {
        return BaseService.get({ url: 'user/get-menu-options' }).then(result => {
            commit('SET_USER_MENU', result);
        });
    },
    getUserContentTypes({ commit }) {
        return BaseService.get({ url: 'content-type/last-version-content-type-for-users-access-right' }).then(result => {
            commit('SET_USER_CONTENT_TYPE', result);
        });
    },
    getUserSavedSearches({ commit }) {
        return BaseService.get({ url: 'user-searches' }).then(result => {
            commit('SET_USER_SAVED_SEARCHES', result);
        });
    },
    getLocalization({ commit }) {
        return BaseService.get({ url: `localization/${Utils.mix.getLanguage()}` }).then(backendTranslations => {
            let translations = {};
            const languageId = Utils.mix.getLanguage();

            let translationImport;
            let frontendTranslations = {};

            switch (languageId) {
                case 1:
                    translationImport = import('@/locales/translations.DE.json');
                    break;
                case 2:
                    translationImport = import('@/locales/translations.EN.json');
                    break;
                case 3:
                    translationImport = import('@/locales/translations.ES.json');
                    break;
                case 4:
                    translationImport = import('@/locales/translations.FR.json');
                    break;
                case 5:
                    translationImport = import('@/locales/translations.IT.json');
                    break;
                case 6:
                    translationImport = import('@/locales/translations.RO.json');
                    break;
                case 7:
                    translationImport = import('@/locales/translations.PT.json');
                    break;
                default:
                    translationImport = import('@/locales/translations.DE.json');
            }

            return translationImport.then(module => {
                frontendTranslations = module.default;
                translations = Object.assign({}, backendTranslations, frontendTranslations);
                commit('SET_LOCALIZATION', translations);
            });
        });
    },
    getOperators({ commit }) {
        return BaseService.get({ url: 'search/operators' }).then(result => {
            commit('SET_SEARCH_OPERATORS', result);
        });
    },
    getStandardFields({ commit }) {
        return BaseService.get({ url: 'content-type/get-standard-fields' }).then(result => {
            commit('SET_SEARCH_STANDAR_FIELDS', result);
        });
    },
    postCulture() {
        return BaseService.post({ url: `culture/${Utils.mix.getLanguage()}` }).then(result => {
        });
    },
    getUserDelegations({ commit }) {
        return BaseService.get({ url: 'delegation/get-users-delegation' }).then(result => {
            commit('SET_USER_DELEGATION', result);
        });
    },
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
};
